import type { SipPlanStatus } from '../../../api/common.types'
import { isPlanDropDelayed, usePlanCountdown } from '../../../../lumpsum/plans-list/timer/shared/usePlanCountdown'
import { IDEAL_FEATURE_ROWS } from '../../../../lumpsum/plans-list/timer/shared/idealFeatureRows'
import {
  TimerPanelCountdownSection,
  TimerPanelFeatureRows,
  TimerPanelFooterCopy,
  TimerPanelFooterHeading,
  TimerPanelShell,
  TimerPanelStrategySection
} from '../../../../lumpsum/plans-list/timer/shared/TimerPanelShell'
import { isSipStrategyInProgress, shouldShowSipStrategyShell } from '../shared/planStatus'
import { PlansScreen } from '../../../../analytics/plansEvents'
import {
  getPlansFlowTheme,
  PlansThemeFlow
} from '../../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'
import { primeTimerCountdownChrome } from '../../../../lumpsum/plans-list/timer/shared/timerCountdownChrome'

const FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.SIP_IDEAL)

type SipIdealTimerPanelProps = {
  planStatus?: SipPlanStatus | null
  timeToPlanDrop?: number | null
  isSubscriptionEligible: boolean
  strategyCta?: unknown
  refetchPlan: () => void
}

/** Strategy / timer / delayed shell before PLAN_ROLLED_OUT (Ideal SIPs tab). */
export function SipIdealTimerPanel({
  planStatus,
  timeToPlanDrop,
  isSubscriptionEligible,
  strategyCta,
  refetchPlan
}: SipIdealTimerPanelProps) {
  const showStrategy = shouldShowSipStrategyShell(planStatus)
  const isDelayed = !showStrategy && isPlanDropDelayed(timeToPlanDrop)
  const countdownParts = usePlanCountdown(showStrategy || isDelayed ? 0 : timeToPlanDrop)

  return (
    <TimerPanelShell
      background={FLOW_THEME.chromeBackground}
      title="Ideal SIPs"
      description="See what your SIP portfolio looks like when it's personalised to your life stage and risk appetite."
      hero={
        showStrategy ? (
          <TimerPanelStrategySection
            promptClassName={FLOW_THEME.primaryDarkTextClassName}
            variant={isSipStrategyInProgress(planStatus) ? 'complete' : 'find'}
            strategyCta={strategyCta}
            refetchPlan={refetchPlan}
            analyticsScreen={PlansScreen.SipIdealTimer}
          />
        ) : (
          <TimerPanelCountdownSection
            {...primeTimerCountdownChrome(FLOW_THEME, {
              readyIn: 'Portfolio under review, your plan will be ready in',
              delayTitle: 'Your ideal SIP plan is delayed',
              delayBody:
                "We need extra time to prepare your Ideal SIP plan. We'll update you as soon as it's ready.",
              unlockPrompt: "Unlock your ideal SIP plan once it's ready"
            })}
            countdownParts={countdownParts}
            isDelayed={isDelayed}
            isSubscriptionEligible={isSubscriptionEligible}
            unlockButtonLabel="Unlock with Prime"
            landingPage="prime"
            analyticsScreen={PlansScreen.SipIdealTimer}
          />
        )
      }
      footer={
        <>
          <TimerPanelFooterHeading>See what your ideal SIPs look like</TimerPanelFooterHeading>
          <TimerPanelFooterCopy>
            We show you the big picture before you make a single move. See what your SIP portfolio
            looks like when it&apos;s personalised to your life stage and risk appetite.
          </TimerPanelFooterCopy>
          <TimerPanelFeatureRows rows={IDEAL_FEATURE_ROWS} iconColor={FLOW_THEME.primary} />
        </>
      }
    />
  )
}
